import React from 'react';
import Button, { ButtonProps } from '../Button/Button';
import VidyardPlayer from '../VidyardPlayer/VidyardPlayer';
import { openVidyardLightboxAndPlay } from '@/utils/openVidyardLightbox';

export type CardProps = {
  theme: 'primary' | 'primary-light' | 'secondary-light' | 'white';
  orientation: 'horizontal' | 'vertical';
  eyebrow?: string;
  header: string;
  description: string | React.ReactNode;
  media?: string;
  videoUuid?: string;
  buttonText?: string; // Only we will use if buttonProps is not provided 
  buttonProps?: ButtonProps; // FullbuttonProps overrides buttonText/linkUrl/linkTarget
  linkUrl?: string;
  linkTarget?: '_blank' | '_self';
};

const Card: React.FC<CardProps> = ({
  theme,
  orientation,
  eyebrow,
  header,
  description,
  media,
  videoUuid,
  buttonText,
  buttonProps,
  linkUrl,
  linkTarget
}) => {
  const themeClass = `card--${theme}`;
  const layoutClass = `card--${orientation}`;

  const hasVideoPoster = Boolean(videoUuid && media);
  const mediaTypeClass = videoUuid ? 'has-video' : media ? 'has-image' : '';

  const handleVideoPosterClick = () => {
    if (videoUuid) {
      openVidyardLightboxAndPlay(videoUuid);
    }
  };

  const configButtonProps: ButtonProps | null = buttonProps
    ? {
        size: 'lg',
        variant: 'border',
        width: 'fit-content',
        ...buttonProps,
        className: `card-btn ${buttonProps.className || ''}`.trim(),
        disabled: buttonProps.disabled ?? false
      }
    : buttonText
    ? {
        label: buttonText,
        size: 'lg',
        variant: 'border',
        width: 'fit-content',
        linkUrl,
        linkTarget,
        className: 'card-btn',
        disabled: false
      }
    : null;

  return (
    <div className={`card ${themeClass} ${layoutClass} ${mediaTypeClass}`}>
      <div className="card__content">
        <div className="card__titleblock">
          {eyebrow && <div className="card__eyebrow">{eyebrow}</div>}
          <h3 className="card__header">{header}</h3>
        </div>
        <p className="card__description">{description}</p>

        {configButtonProps && <Button {...configButtonProps} />}
      </div>

      <div className={`card__media ${videoUuid ? 'card__media--video' : 'card__media--image'}`}>
        {hasVideoPoster ? (
          <>
            <button
              type="button"
              className="card__media-poster"
              onClick={handleVideoPosterClick}
              aria-label={`Play video: ${header}`}
            >
              <img src={media} alt="Card Visual" />
              <span className="card__media-poster-play" aria-hidden="true" />
            </button>
            <div className="card__media-video-hidden" aria-hidden="true">
              <VidyardPlayer uuid={videoUuid!} type="lightbox" autoPlay />
            </div>
          </>
        ) : videoUuid ? (
          <VidyardPlayer uuid={videoUuid} type="lightbox" />
        ) : (
          media && <img src={media} alt="Card Visual" />
        )}
      </div>
    </div>
  );
};

export default Card;
